import { useSyncExternalStore } from 'react';

/**
 * batch-105 — sound notifications for new messages.
 *
 * The operator wants an audible ping when new messages arrive, with a toggle in
 * the top-right. Two distinct chimes:
 *   - 'actionable' — a new message landed in the Needs-reply queue (prominent
 *     two-note rising chime).
 *   - 'other'      — a new inbound that doesn't need a reply (single softer
 *     low note).
 *
 * Detection is timestamp-diff based off the polled /threads/summary clocks
 * (`latest_inbound_at` / `latest_actionable_inbound_at`), so it works no matter
 * which tab is open and never depends on which message it was.
 */

export type ChimeKind = 'actionable' | 'other';

export interface InboundClocks {
  /** Newest inbound message anywhere (ms epoch, or null). */
  inboundAt: number | null;
  /** Newest inbound on an actionable thread (ms epoch, or null). */
  actionableAt: number | null;
}

/** Parse an ISO timestamp to ms, or null. */
export function toMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * Decide which chime (if any) to play given the previous and next inbound
 * clocks. Actionable wins when both advanced (the more important ping). Returns
 * null when nothing newer arrived. Pure — unit-tested.
 */
export function decideChime(prev: InboundClocks, next: InboundClocks): ChimeKind | null {
  const advanced = (a: number | null, b: number | null) => b !== null && (a === null || b > a);
  if (advanced(prev.actionableAt, next.actionableAt)) return 'actionable';
  if (advanced(prev.inboundAt, next.inboundAt)) return 'other';
  return null;
}

/* ---------- localStorage-backed toggle (synced across components) ---------- */

const STORAGE_KEY = 'comms.sound.enabled';
const listeners = new Set<() => void>();
let enabled = readInitial();

function readInitial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function emit() {
  for (const l of listeners) l();
}

export function setSoundEnabled(next: boolean): void {
  enabled = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    // ignore (private mode / disabled storage)
  }
  if (next) primeAudio(); // resume the context within the click gesture
  emit();
}

/** React hook — current toggle state, synced across all consumers in the tab. */
export function useSoundEnabled(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => enabled,
    () => false,
  );
}

/* ---------- Web Audio chimes (no asset; synthesized) ---------- */

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  return ctx;
}

/** Resume the AudioContext (must run inside a user gesture, e.g. the toggle). */
export function primeAudio(): void {
  const c = audioCtx();
  if (c && c.state === 'suspended') void c.resume();
}

/** Play one short tone with a click-free envelope. */
function tone(c: AudioContext, freq: number, startSec: number, durSec: number, peak: number): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  const t0 = c.currentTime + startSec;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + durSec + 0.02);
}

/** Play the chime for a new message of the given kind. No-op if audio is unavailable. */
export function playChime(kind: ChimeKind): void {
  const c = audioCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  if (kind === 'actionable') {
    // Rising two-note chime — attention-getting.
    tone(c, 660, 0, 0.18, 0.18);
    tone(c, 990, 0.14, 0.22, 0.18);
  } else {
    // Single soft low note — informational.
    tone(c, 440, 0, 0.16, 0.08);
  }
}
